// Enhanced real-time notification system for APN
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  onSnapshot,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';

// Enhanced notification types
export const NOTIFICATION_TYPES = {
  ANNOUNCEMENT: 'announcement',
  JOB_APPLICATION: 'job_application',
  PROJECT_APPLICATION: 'project_application',
  EVENT_REGISTRATION: 'event_registration',
  EVENT_CREATED: 'event_created',
  EVENT_UPDATED: 'event_updated',
  EVENT_REMINDER: 'event_reminder',
  MEMBERSHIP_APPROVED: 'membership_approved',
  GROUP_INVITATION: 'group_invitation',
  GROUP_NEW_POST: 'group_new_post',
  GROUP_NEW_MEMBER: 'group_new_member',
  GROUP_EVENT_CREATED: 'group_event_created',
  FORUM_REPLY: 'forum_reply',
  FORUM_LIKE: 'forum_like',
  SYSTEM_UPDATE: 'system_update',
  PAYMENT_RECEIVED: 'payment_received',
  PAYMENT_FAILED: 'payment_failed',
  // Admin-only notification types
  ADMIN_NEW_USER: 'admin_new_user',
  ADMIN_NEW_EVENT: 'admin_new_event',
  ADMIN_NEW_JOB: 'admin_new_job',
  ADMIN_NEW_PROJECT: 'admin_new_project',
  ADMIN_NEW_GROUP: 'admin_new_group',
  ADMIN_PENDING_APPROVAL: 'admin_pending_approval',
  ADMIN_SYSTEM_ALERT: 'admin_system_alert'
};

// Create notification
export const createNotification = async (notificationData) => {
  try {
    const notification = {
      ...notificationData,
      read: false,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'notifications'), notification);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Get notifications for a user
export const getUserNotifications = async (userId, limitCount = 20) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadNotificationCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    throw error;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read for a user
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false)
    );

    const querySnapshot = await getDocs(q);
    const updatePromises = querySnapshot.docs.map(docSnapshot => 
      updateDoc(doc(db, 'notifications', docSnapshot.id), {
        read: true,
        readAt: serverTimestamp()
      })
    );

    await Promise.all(updatePromises);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

// Send notification to user
export const sendNotificationToUser = async (userId, type, title, message, actionUrl = null, metadata = {}) => {
  try {
    return await createNotification({
      userId,
      type,
      title,
      message,
      actionUrl,
      metadata
    });
  } catch (error) {
    console.error('Error sending notification to user:', error);
    throw error;
  }
};

// Send notification to multiple users
export const sendNotificationToUsers = async (userIds, type, title, message, actionUrl = null, metadata = {}) => {
  try {
    const notifications = userIds.map(userId => ({
      userId,
      type,
      title,
      message,
      actionUrl,
      metadata,
      read: false,
      createdAt: serverTimestamp()
    }));

    const promises = notifications.map(notification => 
      addDoc(collection(db, 'notifications'), notification)
    );

    const results = await Promise.all(promises);
    return results.map(docRef => docRef.id);
  } catch (error) {
    console.error('Error sending notifications to users:', error);
    throw error;
  }
};

// Send announcement notification to all members
export const sendAnnouncementNotification = async (title, message, actionUrl = null) => {
  try {
    // Get all approved members
    const { getAllUsers } = await import('./firestoreService');
    const users = await getAllUsers(1000);
    const approvedUsers = users.filter(user => user.status === 'approved');
    const userIds = approvedUsers.map(user => user.id);

    if (userIds.length === 0) {
      throw new Error('No approved members found');
    }

    return await sendNotificationToUsers(
      userIds,
      NOTIFICATION_TYPES.ANNOUNCEMENT,
      title,
      message,
      actionUrl
    );
  } catch (error) {
    console.error('Error sending announcement notification:', error);
    throw error;
  }
};

// Real-time notification listener
export const subscribeToUserNotifications = (userId, callback) => {
  try {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(notifications);
    }, (error) => {
      console.error('Error in notification listener:', error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up notification listener:', error);
    return () => {}; // Return empty function if setup fails
  }
};

// Group-related notifications
export const notifyGroupMembers = async (groupId, type, title, message, actionUrl = null, excludeUserId = null) => {
  try {
    const { getGroupById } = await import('./firestoreService');
    const group = await getGroupById(groupId);

    if (!group || !group.members) {
      throw new Error('Group not found or has no members');
    }

    // Filter out the user who triggered the notification
    const memberIds = group.members.filter(memberId => memberId !== excludeUserId);

    if (memberIds.length === 0) {
      return [];
    }

    return await sendNotificationToUsers(
      memberIds,
      type,
      title,
      message,
      actionUrl,
      { groupId, groupName: group.name }
    );
  } catch (error) {
    console.error('Error notifying group members:', error);
    throw error;
  }
};

// Event-related notifications
export const notifyEventFollowers = async (eventId, type, title, message, actionUrl = null) => {
  try {
    const { getEventById, getEventRegistrations } = await import('./firestoreService');
    const event = await getEventById(eventId);

    if (!event) {
      throw new Error('Event not found');
    }

    // Get all registered users for the event
    const registrations = await getEventRegistrations(eventId);
    const userIds = registrations.map(reg => reg.userId).filter(Boolean);

    if (userIds.length === 0) {
      return [];
    }

    return await sendNotificationToUsers(
      userIds,
      type,
      title,
      message,
      actionUrl,
      { eventId, eventTitle: event.title }
    );
  } catch (error) {
    console.error('Error notifying event followers:', error);
    throw error;
  }
};

// Notification templates for common actions
export const NOTIFICATION_TEMPLATES = {
  [NOTIFICATION_TYPES.JOB_APPLICATION]: {
    title: 'New Job Application',
    getMessage: (jobTitle) => `You have received a new application for your job posting: ${jobTitle}`
  },
  [NOTIFICATION_TYPES.PROJECT_APPLICATION]: {
    title: 'New Project Application',
    getMessage: (projectTitle) => `You have received a new application for your project: ${projectTitle}`
  },
  [NOTIFICATION_TYPES.EVENT_REGISTRATION]: {
    title: 'Event Registration Confirmed',
    getMessage: (eventTitle) => `Your registration for ${eventTitle} has been confirmed.`
  },
  [NOTIFICATION_TYPES.MEMBERSHIP_APPROVED]: {
    title: 'Membership Approved!',
    getMessage: () => 'Welcome to APN! Your membership has been approved and you now have full access to all features.'
  },
  [NOTIFICATION_TYPES.GROUP_INVITATION]: {
    title: 'Group Invitation',
    getMessage: (groupName) => `You have been invited to join the group: ${groupName}`
  },
  [NOTIFICATION_TYPES.FORUM_REPLY]: {
    title: 'New Forum Reply',
    getMessage: (topicTitle) => `Someone replied to your post in: ${topicTitle}`
  }
};

// Helper function to send templated notification
export const sendTemplatedNotification = async (userId, type, data, actionUrl = null) => {
  try {
    const template = NOTIFICATION_TEMPLATES[type];
    if (!template) {
      throw new Error(`No template found for notification type: ${type}`);
    }

    const title = template.title;
    const message = template.getMessage(data);

    return await sendNotificationToUser(userId, type, title, message, actionUrl, data);
  } catch (error) {
    console.error('Error sending templated notification:', error);
    throw error;
  }
};

// Specific notification helpers
export const notifyNewGroupPost = async (groupId, postTitle, authorName, excludeUserId = null) => {
  return await notifyGroupMembers(
    groupId,
    NOTIFICATION_TYPES.GROUP_NEW_POST,
    'New Discussion in Group',
    `${authorName} started a new discussion: "${postTitle}"`,
    `/groups/${groupId}`,
    excludeUserId
  );
};

export const notifyNewGroupMember = async (groupId, newMemberName, excludeUserId = null) => {
  return await notifyGroupMembers(
    groupId,
    NOTIFICATION_TYPES.GROUP_NEW_MEMBER,
    'New Group Member',
    `${newMemberName} joined the group`,
    `/groups/${groupId}`,
    excludeUserId
  );
};

export const notifyEventCreated = async (eventId, eventTitle, creatorName) => {
  try {
    // Notify all approved members about new events
    const { getAllUsers } = await import('./firestoreService');
    const users = await getAllUsers(1000);
    const approvedUsers = users.filter(user => user.status === 'approved');
    const userIds = approvedUsers.map(user => user.id);

    return await sendNotificationToUsers(
      userIds,
      NOTIFICATION_TYPES.EVENT_CREATED,
      'New Event Available',
      `${creatorName} created a new event: "${eventTitle}"`,
      `/events/${eventId}`,
      { eventId, eventTitle }
    );
  } catch (error) {
    console.error('Error notifying about new event:', error);
    throw error;
  }
};

// Notify group members when a group creates an event
export const notifyGroupEventCreated = async (groupId, eventId, eventTitle, creatorName) => {
  try {
    console.log('🔔 Notifying group members about new event:', { groupId, eventId, eventTitle });

    const { getGroupById } = await import('./firestoreService');
    const group = await getGroupById(groupId);

    if (!group || !group.members || group.members.length === 0) {
      console.log('No group members to notify');
      return [];
    }

    // Filter out the creator from notifications
    const memberIds = group.members.filter(memberId => memberId !== creatorName);

    if (memberIds.length === 0) {
      console.log('No other members to notify (creator excluded)');
      return [];
    }

    return await sendNotificationToUsers(
      memberIds,
      NOTIFICATION_TYPES.GROUP_EVENT_CREATED,
      'New Group Event',
      `${group.name} has a new event: "${eventTitle}"`,
      `/events/${eventId}`,
      {
        eventId,
        eventTitle,
        groupId,
        groupName: group.name,
        creatorName
      }
    );
  } catch (error) {
    console.error('Error notifying group members about new event:', error);
    throw error;
  }
};

export const notifyEventReminder = async (eventId, reminderType = '24h') => {
  const reminderMessages = {
    '24h': 'Your event is tomorrow!',
    '1h': 'Your event starts in 1 hour!',
    '15m': 'Your event starts in 15 minutes!'
  };

  return await notifyEventFollowers(
    eventId,
    NOTIFICATION_TYPES.EVENT_REMINDER,
    'Event Reminder',
    reminderMessages[reminderType] || 'Event reminder',
    `/events/${eventId}`
  );
};

// ============ ADMIN-ONLY NOTIFICATIONS ============

// Helper function to get all admin users
const getAdminUsers = async () => {
  try {
    const { getAllUsers } = await import('./firestoreService');
    const users = await getAllUsers(1000);
    const adminUsers = users.filter(user => user.role === 'admin');
    return adminUsers.map(user => user.id);
  } catch (error) {
    console.error('Error getting admin users:', error);
    return [];
  }
};

// Notify admins when a new user registers
export const notifyAdminsNewUser = async (newUserName, newUserEmail) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_NEW_USER,
      'New User Registration',
      `${newUserName} (${newUserEmail}) has registered and needs approval`,
      '/admin?tab=pending',
      { newUserName, newUserEmail }
    );
  } catch (error) {
    console.error('Error notifying admins about new user:', error);
    throw error;
  }
};

// Notify admins when a new event is created
export const notifyAdminsNewEvent = async (eventTitle, creatorName) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_NEW_EVENT,
      'New Event Created',
      `${creatorName} created a new event: "${eventTitle}"`,
      '/admin?tab=content',
      { eventTitle, creatorName }
    );
  } catch (error) {
    console.error('Error notifying admins about new event:', error);
    throw error;
  }
};

// Notify admins when a new job is posted
export const notifyAdminsNewJob = async (jobTitle, posterName) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_NEW_JOB,
      'New Job Posted',
      `${posterName} posted a new job: "${jobTitle}"`,
      '/admin?tab=content',
      { jobTitle, posterName }
    );
  } catch (error) {
    console.error('Error notifying admins about new job:', error);
    throw error;
  }
};

// Notify admins when a new project is posted
export const notifyAdminsNewProject = async (projectTitle, posterName) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_NEW_PROJECT,
      'New Project Posted',
      `${posterName} posted a new project: "${projectTitle}"`,
      '/admin?tab=content',
      { projectTitle, posterName }
    );
  } catch (error) {
    console.error('Error notifying admins about new project:', error);
    throw error;
  }
};

// Notify admins when a new group is created
export const notifyAdminsNewGroup = async (groupName, creatorName) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_NEW_GROUP,
      'New Group Created',
      `${creatorName} created a new group: "${groupName}"`,
      '/admin?tab=content',
      { groupName, creatorName }
    );
  } catch (error) {
    console.error('Error notifying admins about new group:', error);
    throw error;
  }
};

// Notify admins about pending approvals (batch notification)
export const notifyAdminsPendingApprovals = async (pendingCount) => {
  try {
    const adminIds = await getAdminUsers();

    if (adminIds.length === 0) {
      console.log('No admin users found to notify');
      return;
    }

    return await sendNotificationToUsers(
      adminIds,
      NOTIFICATION_TYPES.ADMIN_PENDING_APPROVAL,
      'Pending User Approvals',
      `${pendingCount} user${pendingCount > 1 ? 's' : ''} waiting for approval`,
      '/admin?tab=pending',
      { pendingCount }
    );
  } catch (error) {
    console.error('Error notifying admins about pending approvals:', error);
    throw error;
  }
};





export const notifyPaymentReceived = async (userId, amount, eventTitle = null, membershipTier = null) => {
  const message = eventTitle
    ? `Payment of $${amount} received for event: ${eventTitle}`
    : `Payment of $${amount} received for ${membershipTier} membership`;

  return await sendNotificationToUser(
    userId,
    NOTIFICATION_TYPES.PAYMENT_RECEIVED,
    'Payment Confirmed',
    message,
    eventTitle ? '/events' : '/membership'
  );
};

export default {
  createNotification,
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  sendNotificationToUser,
  sendNotificationToUsers,
  sendAnnouncementNotification,
  sendTemplatedNotification,
  subscribeToUserNotifications,
  notifyGroupMembers,
  notifyEventFollowers,
  notifyNewGroupPost,
  notifyNewGroupMember,
  notifyEventCreated,
  notifyEventReminder,
  notifyPaymentReceived,
  // Admin-only notifications
  notifyAdminsNewUser,
  notifyAdminsNewEvent,
  notifyAdminsNewJob,
  notifyAdminsNewProject,
  notifyAdminsNewGroup,
  notifyAdminsPendingApprovals,
  NOTIFICATION_TYPES,
  NOTIFICATION_TEMPLATES
};
